import React from 'react';
import { Card, Descriptions, Typography, Button } from 'antd';
import { SendOutlined } from '@ant-design/icons';
import type { AutofacturaOrderSummary, AutofacturaGenerarRequest } from '../../types/autofactura';
import type { CatalogoRegimenFiscal, CatalogoUsoCFDI } from '../../types/facturacion';

const { Text } = Typography;

interface ConfirmationStepProps {
  order: AutofacturaOrderSummary;
  fiscalData: AutofacturaGenerarRequest;
  regimenesFiscales?: CatalogoRegimenFiscal[];
  usosCFDI?: CatalogoUsoCFDI[];
  onConfirm: () => void;
  onBack: () => void;
  submitting: boolean;
}

const formaPagoLabels: Record<string, string> = {
  '01': 'Efectivo',
  '02': 'Cheque nominativo',
  '03': 'Transferencia electrónica',
  '04': 'Tarjeta de crédito',
  '28': 'Tarjeta de débito',
  '99': 'Por definir',
};

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  order,
  fiscalData,
  regimenesFiscales,
  usosCFDI,
  onConfirm,
  onBack,
  submitting,
}) => {
  const regimenLabel = regimenesFiscales?.find(r => r.clave === fiscalData.regimenFiscal);
  const usoLabel = usosCFDI?.find(u => u.clave === fiscalData.usoCFDI);

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>Datos de la orden</Text>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="Orden #">{order.orderNumber}</Descriptions.Item>
          {order.formaPago && (
            <Descriptions.Item label="Método de pago">
              {formaPagoLabels[order.formaPago] || order.formaPago}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Subtotal">${order.subtotal.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="IVA">${order.impuestos.toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Total">
            <Text strong>${order.total.toFixed(2)}</Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>Datos fiscales del receptor</Text>
        <Descriptions column={1} size="small">
          <Descriptions.Item label="RFC">{fiscalData.rfc}</Descriptions.Item>
          <Descriptions.Item label="Razón Social">{fiscalData.razonSocial}</Descriptions.Item>
          <Descriptions.Item label="Régimen Fiscal">
            {regimenLabel ? `${regimenLabel.clave} - ${regimenLabel.descripcion}` : fiscalData.regimenFiscal}
          </Descriptions.Item>
          <Descriptions.Item label="Uso CFDI">
            {usoLabel ? `${usoLabel.clave} - ${usoLabel.descripcion}` : fiscalData.usoCFDI}
          </Descriptions.Item>
          <Descriptions.Item label="Código Postal">{fiscalData.codigoPostal}</Descriptions.Item>
          {fiscalData.email && (
            <Descriptions.Item label="Email">{fiscalData.email}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Button
        type="primary"
        block
        size="large"
        icon={<SendOutlined />}
        onClick={onConfirm}
        loading={submitting}
        style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)', marginBottom: 8 }}
      >
        Generar factura
      </Button>
      <Button block size="large" onClick={onBack} disabled={submitting}>
        Regresar
      </Button>
    </div>
  );
};

export default ConfirmationStep;
